import React, { FC, useState } from 'react';
import {
  ICalendarUrl, IMailAuth, setClass, TCalendarProvider,
} from '@utils';
import { isMobile } from 'react-device-detect';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { Button } from '../button';
import { CalendarTitle } from './CalendarTitle';
import '../../styles/components/newCalendarField.scss';
import { CalendarUrl } from './CalendarUrl';
import { ButtonGroup, IButton } from '../buttonGroup';
import {
  MailLogo, OutlookLogo, YandexLogo, ThunderbirdLogo, GoogleLogo, OnboardingIcon,
} from '../svgs';
import { IColor } from '../../utils/types/colors';
import { Input } from '../input';
import { ICalendarError } from '../../utils/types/calendarError';
import { Loader } from '../loader';

interface NewCalendarFieldProps {
  calendar: ICalendarUrl;
  colors: IColor[];
  calendarProvider: TCalendarProvider;
  mailAuth: IMailAuth;
  onChangeCalendar: (calendar: ICalendarUrl) => void;
  onChangeCalendarProvider: (urlType: TCalendarProvider) => void;
  onSave: () => void;
  onCancel: () => void;
  getColor: (name: string) => IColor;
  changeMailAuth: (value: IMailAuth) => void;
  initAuth: () => void;
  loading: boolean;
}

export const NewCalendarField: FC<NewCalendarFieldProps> = ({
  calendar, colors, calendarProvider, onChangeCalendar, changeMailAuth, loading,
  onCancel, onSave: save, onChangeCalendarProvider, getColor, mailAuth, initAuth,
}) => {
  const navigate = useNavigate();
  const [err, setErr] = useState<ICalendarError>({
    title: false, uri: false, login: false, pass: false,
  });

  const setUriError = (value: boolean) => {
    setErr(prev => ({ ...prev, uri: value }));
  };

  const setTitleError = (value: boolean) => {
    setErr(prev => ({ ...prev, title: value }));
  };

  const onSave = () => {
    const error: ICalendarError = {
      title: calendar.title.trim().length === 0,
      uri: calendar.calendarUri.trim().length === 0,
      login: calendarProvider === 'MAIL' && (mailAuth?.email?.trim()?.length === 0 ?? false),
      pass: calendarProvider === 'MAIL' && (mailAuth?.password?.trim()?.length === 0 ?? false),
    };
    setErr(error);

    if (!(error.title || error.uri || error.login || error.pass)) {
      save();
    } else {
      toast.error('Заполните пустые поля');
    }
  };

  const onChangeEmail = (value: string) => {
    changeMailAuth({ password: mailAuth.password, email: value });
    setErr(prev => ({ ...prev, login: false }));
  };

  const onChangePassword = (value: string) => {
    changeMailAuth({ email: mailAuth.email, password: value });
    setErr(prev => ({ ...prev, pass: false }));
  };

  const iconButtons: IButton[] = [
    {
      id: 'THUNDERBIRD',
      icon: <ThunderbirdLogo />,
      onClick: () => { onChangeCalendarProvider('THUNDERBIRD'); },
    },
    {
      id: 'GOOGLE',
      icon: <GoogleLogo />,
      onClick: () => onChangeCalendarProvider('GOOGLE'),
    },
    {
      id: 'MAIL',
      icon: <MailLogo />,
      onClick: () => {
        onChangeCalendarProvider('MAIL');
        initAuth();
      },
    },
    {
      id: 'OUTLOOK',
      icon: <OutlookLogo />,
      onClick: () => onChangeCalendarProvider('OUTLOOK'),
    },
    {
      id: 'YANDEX',
      icon: <YandexLogo />,
      onClick: () => onChangeCalendarProvider('YANDEX'),
    },
  ];

  const showCalendarHelp = () => {
    navigate(`/onboarding/${calendarProvider.toLowerCase()}`);
  };

  return (
    loading
      ? (
        <div className="calendar-loader-wrapper">
          <Loader />
        </div>
      )
      : (
        <div className={setClass('new-calendar-area', isMobile && 'mobile')}>
          <CalendarTitle
            error={err.title}
            setErr={setTitleError}
            getColor={getColor}
            colors={colors}
            calendar={calendar}
            changing
            onChange={onChangeCalendar}
          />
          <div className="logo-group">
            <ButtonGroup
              buttons={iconButtons}
              selectedId={calendarProvider}
            />
            {!isMobile && <OnboardingIcon divClassName="onboarding-icon" onClick={showCalendarHelp} />}
          </div>
          <CalendarUrl
            setErr={setUriError}
            error={err.uri}
            calendar={calendar}
            onChange={onChangeCalendar}
            newCalendar
          />
          { calendarProvider === 'MAIL' && (
            <div className={setClass('mail-auth', isMobile && 'mobile')}>
              <Input
                variant="underlined"
                classes="auth-field big-padding"
                placeholder="Введите логин"
                error={err.login}
                value={mailAuth.email}
                onChange={onChangeEmail}
              />
              <Input
                variant="underlined"
                classes="auth-field big-padding"
                placeholder="Введите пароль"
                error={err.pass}
                value={mailAuth.password}
                onChange={onChangePassword}
              />
            </div>
          )}
          <div className="calendar-buttons-group">
            <Button
              title="Отмена"
              className={setClass('outlined small', isMobile && 'mobile-button')}
              onClick={onCancel}
            />
            <Button
              title="Сохранить"
              className={setClass('small', isMobile && 'mobile-button')}
              onClick={onSave}
            />
          </div>
        </div>
      )
  );
};
